// Handing a link to someone, by whatever means the device actually has.
//
// The invoice link exists to be texted. On a phone that means the native
// share sheet -- Messages, WhatsApp, wherever the customer is -- which is
// what navigator.share opens. On a desktop browser without it, the next
// best thing is the link on the clipboard, ready to paste.

export interface ShareRequest {
  title: string;
  /** The line that goes above the link, so the message reads as a message. */
  text: string;
  url: string;
}

export type ShareOutcome = 'shared' | 'copied' | 'dismissed' | 'failed';

/** Whether this browser can open a native share sheet. */
export const canShare = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.share === 'function';

/**
 * Share a link, falling back to the clipboard.
 *
 * Returns what actually happened rather than throwing, because every
 * outcome here is a normal one the caller wants to report differently --
 * including "they opened the sheet and changed their mind", which must not
 * be reported as an error or, worse, silently retried as a copy.
 */
export const shareLink = async (request: ShareRequest): Promise<ShareOutcome> => {
  if (canShare()) {
    try {
      await navigator.share(request);
      return 'shared';
    } catch (e: any) {
      // The share sheet was dismissed. Nothing went wrong and nothing else
      // should happen.
      if (e?.name === 'AbortError') return 'dismissed';
      // Anything else (no handler registered, permission refused) is worth
      // falling through to the clipboard for.
    }
  }

  try {
    await navigator.clipboard.writeText(`${request.text}\n${request.url}`);
    return 'copied';
  } catch {
    return 'failed';
  }
};
